import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import './DatePicker.css';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const pad = (n) => String(n).padStart(2, '0');

/** Parse a 'YYYY-MM-DD' string into { y, m (0-based), d }, or null. */
function parseISO(value) {
  if (!value || typeof value !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  if (mo < 0 || mo > 11 || d < 1 || d > 31) return null;
  return { y, m: mo, d };
}

const toISO = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

function formatDisplay(value) {
  const p = parseISO(value);
  if (!p) return '';
  return `${MONTHS_SHORT[p.m]} ${p.d}, ${p.y}`;
}

/**
 * Custom date picker with a calendar popover. Trigger reuses the shared
 * `auth-picker` styling (matches CompactSelectPicker), calendar is themed for
 * light/dark. Emits 'YYYY-MM-DD' strings (or null when cleared) via onChange.
 */
export default function DatePicker({
  value,
  onChange,
  disabled = false,
  className = '',
  placeholder = 'Select date',
  menuMinWidth = 248,
  'aria-label': ariaLabel = 'Select date',
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: menuMinWidth });
  const triggerRef = useRef(null);

  const selected = parseISO(value);
  const now = new Date();
  const [viewYear, setViewYear] = useState(selected ? selected.y : now.getFullYear());
  const [viewMonth, setViewMonth] = useState(selected ? selected.m : now.getMonth());

  const close = useCallback(() => setOpen(false), []);

  const measure = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const menuWidth = Math.max(r.width, menuMinWidth);
    const vw = window.innerWidth;
    const left = r.left + menuWidth > vw - 8 ? Math.max(8, r.right - menuWidth) : r.left;
    setPos({ top: r.bottom + 6, left, width: menuWidth });
  }, [menuMinWidth]);

  useLayoutEffect(() => {
    if (!open) return;
    measure();
    // Sync the visible month to the selected date each time we open.
    const p = parseISO(value);
    if (p) {
      setViewYear(p.y);
      setViewMonth(p.m);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const onResizeScroll = () => measure();
    window.addEventListener('resize', onResizeScroll);
    window.addEventListener('scroll', onResizeScroll, true);
    return () => {
      window.removeEventListener('resize', onResizeScroll);
      window.removeEventListener('scroll', onResizeScroll, true);
    };
  }, [open, measure]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);

  const toggle = () => {
    if (disabled) return;
    setOpen((o) => !o);
  };

  const prevMonth = () => {
    setViewMonth((m) => {
      if (m === 0) {
        setViewYear((y) => y - 1);
        return 11;
      }
      return m - 1;
    });
  };
  const nextMonth = () => {
    setViewMonth((m) => {
      if (m === 11) {
        setViewYear((y) => y + 1);
        return 0;
      }
      return m + 1;
    });
  };

  const pick = (d) => {
    onChange?.(toISO(viewYear, viewMonth, d));
    close();
  };

  const clear = () => {
    onChange?.(null);
    close();
  };

  const goToday = () => {
    const t = new Date();
    onChange?.(toISO(t.getFullYear(), t.getMonth(), t.getDate()));
    close();
  };

  // Build the day grid: leading blanks + days of month.
  const firstWeekday = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);

  const isToday = (d) =>
    d && now.getFullYear() === viewYear && now.getMonth() === viewMonth && now.getDate() === d;
  const isSelected = (d) =>
    d && selected && selected.y === viewYear && selected.m === viewMonth && selected.d === d;

  const label = formatDisplay(value);

  const popover =
    typeof document !== 'undefined' && document.body && open
      ? createPortal(
          <>
            <div
              className="date-picker-backdrop"
              aria-hidden
              onMouseDown={(e) => {
                e.preventDefault();
                close();
              }}
            />
            <div
              className="date-picker-menu"
              role="dialog"
              aria-label={ariaLabel}
              style={{
                position: 'fixed',
                top: pos.top,
                left: pos.left,
                minWidth: pos.width,
                zIndex: 10040,
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="date-picker-header">
                <button
                  type="button"
                  className="date-picker-nav"
                  onClick={prevMonth}
                  aria-label="Previous month"
                >
                  <ChevronLeft size={16} />
                </button>
                <span className="date-picker-title">
                  {MONTHS[viewMonth]} {viewYear}
                </span>
                <button
                  type="button"
                  className="date-picker-nav"
                  onClick={nextMonth}
                  aria-label="Next month"
                >
                  <ChevronRight size={16} />
                </button>
              </div>

              <div className="date-picker-weekdays">
                {WEEKDAYS.map((w) => (
                  <span key={w} className="date-picker-weekday">
                    {w}
                  </span>
                ))}
              </div>

              <div className="date-picker-grid">
                {cells.map((d, i) => (
                  <div key={i} className="date-picker-cell">
                    {d ? (
                      <button
                        type="button"
                        className={`date-picker-day${isSelected(d) ? ' date-picker-day--selected' : ''}${
                          isToday(d) && !isSelected(d) ? ' date-picker-day--today' : ''
                        }`}
                        onClick={() => pick(d)}
                      >
                        {d}
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>

              <div className="date-picker-footer">
                <button type="button" className="date-picker-action" onClick={goToday}>
                  Today
                </button>
                <button
                  type="button"
                  className="date-picker-action date-picker-action--muted"
                  onClick={clear}
                  disabled={!value}
                >
                  Clear
                </button>
              </div>
            </div>
          </>,
          document.body
        )
      : null;

  return (
    <>
      <div className={`auth-picker ${className}`.trim()} ref={triggerRef}>
        <button
          type="button"
          disabled={disabled}
          onClick={toggle}
          className="auth-picker__trigger"
          aria-expanded={open}
          aria-haspopup="dialog"
          aria-label={ariaLabel}
        >
          <span
            className={`auth-picker__trigger-label${label ? '' : ' date-picker-placeholder'}`}
          >
            {label || placeholder}
          </span>
          <Calendar size={14} className="date-picker-trigger-icon" aria-hidden />
        </button>
      </div>
      {popover}
    </>
  );
}
